import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import api from "../lib/api";
import { Sparkles, TrendingUp, Trophy, Target, Share2, Loader2, Link as LinkIcon } from "lucide-react";
import ArchetypeShareCard from "./ArchetypeShareCard";

export default function ArchetypeCard({ userIdOrSlug, owner = null, compact = false, className = "", shareable = true }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const captureRef = useRef(null);

  useEffect(() => {
    if (!userIdOrSlug) return;
    api.get(`/collectors/${encodeURIComponent(userIdOrSlug)}/archetype`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [userIdOrSlug]);

  const handleShare = async () => {
    if (!data || exporting) return;
    setExporting(true);
    setShowCapture(true);
    // Wait for the off-screen card to mount + images to load
    await new Promise((r) => setTimeout(r, 150));
    try {
      const node = captureRef.current;
      if (!node) throw new Error("capture node missing");
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#06060a",
        width: 1080,
        height: 1080,
      });
      const slugPart = (owner?.profile_slug || data?.user_id || "collector").toString().replace(/[^a-z0-9-]/gi, "-");
      const archName = (data.archetype?.name || "archetype").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      const link = document.createElement("a");
      link.download = `castfolio-${archName}-${slugPart}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Archetype card saved — share it anywhere!");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't export archetype card");
    } finally {
      setExporting(false);
      setShowCapture(false);
    }
  };

  const handleCopyLink = async () => {
    const slug = owner?.profile_slug;
    if (!slug) {
      toast.error("Public link unavailable — set a profile slug first");
      return;
    }
    const url = `${window.location.origin}/api/share/c/${slug}/archetype`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied!");
    } catch {
      toast.error("Couldn't copy — link: " + url);
    }
  };

  if (error || !data) return null;
  const { archetype, stats } = data;

  return (
    <>
      <div
        data-testid="archetype-card"
        className={`relative overflow-hidden rounded-2xl ring-1 ring-white/10 ${className}`}
        style={{ background: "linear-gradient(135deg, #0a0a0b 0%, #1a1a1d 60%, #2d0b3e 100%)" }}
      >
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-fuchsia-500/15 blur-3xl pointer-events-none" />
        <div className="relative p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <p className="text-amber-300 text-[11px] uppercase tracking-[0.3em] font-semibold flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Collector Archetype
            </p>
            {shareable && (
              <div className="flex items-center gap-1.5">
                {owner?.profile_slug && (
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    data-testid="copy-archetype-link-btn"
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 transition"
                    title="Copy public archetype link"
                  >
                    <LinkIcon className="w-3 h-3" />
                    Link
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleShare}
                  disabled={exporting}
                  data-testid="share-archetype-btn"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 transition disabled:opacity-50"
                  title="Save as PNG to share"
                >
                  {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />}
                  Share
                </button>
              </div>
            )}
          </div>
          <h3 className="font-heading text-2xl sm:text-3xl font-black text-white mt-2 leading-tight">{archetype.name}</h3>
          <p className="text-zinc-300 text-sm mt-2">{archetype.tagline}</p>
          {!compact && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-white/10">
              <Stat icon={Target} label="Pieces" value={stats.pieces.toLocaleString()} />
              <Stat icon={Sparkles} label="Brands" value={stats.brand_diversity} />
              <Stat icon={Trophy} label="Chases" value={stats.chase_count} accent="text-amber-300" />
              <Stat icon={TrendingUp} label="Grails" value={stats.grail_count} accent="text-rose-300" />
            </div>
          )}
        </div>
      </div>

      {/* Off-screen capture target — only mounted while exporting */}
      {showCapture && (
        <div
          aria-hidden="true"
          style={{ position: "fixed", left: -99999, top: 0, zIndex: -1, pointerEvents: "none" }}
        >
          <ArchetypeShareCard archetype={archetype} stats={stats} owner={owner} innerRef={captureRef} />
        </div>
      )}
    </>
  );
}

function Stat({ icon: Icon, label, value, accent = "text-white" }) {
  return (
    <div>
      <Icon className="w-4 h-4 text-zinc-500 mb-1" />
      <div className={`text-2xl font-black ${accent}`}>{value}</div>
      <div className="text-zinc-500 text-[10px] uppercase tracking-widest">{label}</div>
    </div>
  );
}
